import type { ObraDetail, ReportSummary } from '@obras/shared';
import {
  daysBetween,
  formatCoordinate,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatDays,
  formatPercent,
  ratio,
} from '@obras/shared';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  ArchiveRestore,
  ArrowLeft,
  Building2,
  CalendarClock,
  ChartLine,
  Expand,
  Eye,
  EyeOff,
  FileText,
  HardHat,
  Images,
  MapPin,
  Paperclip,
  PauseCircle,
  Pencil,
  Plus,
  Trash2,
  UserCog,
  type LucideIcon,
} from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';

import { AppShell } from '../components/AppShell.js';
import { Badge, ObraStatusBadge, VisitStatusBadge } from '../components/ui/Badge.js';
import { Button } from '../components/ui/Button.js';
import { Card, CardHeader, Kpi } from '../components/ui/Card.js';
import { Avatar, ErrorState, ProgressBar, Skeleton } from '../components/ui/Feedback.js';
import { Lightbox, useLightbox, type LightboxItem } from '../components/ui/Lightbox.js';
import { Modal } from '../components/ui/Modal.js';
import { Tab, TabList } from '../components/ui/Tabs.js';
import { ObraFilesTab } from '../features/documentos/ObraFilesTab.js';
import { ObraTeamTab } from '../features/obras/ObraTeamTab.js';
import { ObraFormModal } from '../features/obras/ObraFormModal.js';
import { useDeleteObra, useObra } from '../features/obras/api.js';
import { ObraFinanceTab, ObraGalleryTab, ObraStoppagesTab } from '../features/reports/ObraTabs.js';
import { ReportFormModal } from '../features/reports/ReportFormModal.js';
import { ReportTimeline } from '../features/reports/ReportTimeline.js';
import { useRemoveReport, useReports, useRestoreReport } from '../features/reports/api.js';
import { useAuth } from '../lib/auth.js';
import { cn } from '../lib/cn.js';
import { TextAreaField } from '../components/ui/Field.js';

const ICON = { strokeWidth: 1.5 } as const;

type TabId =
  'visao' | 'financeiro' | 'relatorios' | 'galeria' | 'paralisacoes' | 'equipe' | 'documentos';

const TABS: readonly { value: TabId; label: string; icon: LucideIcon; phase?: string }[] = [
  { value: 'visao', label: 'Visão geral', icon: HardHat },
  { value: 'financeiro', label: 'Financeiro', icon: ChartLine },
  { value: 'relatorios', label: 'Relatórios', icon: FileText },
  { value: 'galeria', label: 'Galeria', icon: Images },
  { value: 'paralisacoes', label: 'Paralisações', icon: PauseCircle },
  { value: 'equipe', label: 'Equipe', icon: UserCog },
  { value: 'documentos', label: 'Arquivos', icon: Paperclip },
];

function DataRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-[7px]">
      <span className="text-[13px] text-muted-foreground">{label}</span>
      <span className="tabular text-right text-[13px] font-medium">{value}</span>
    </div>
  );
}

/** Bloco lateral: título curto e conteúdo, sem o peso de um CardHeader. */
function SideCard({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <Card className="p-5">
      <p className="mb-3 flex items-center gap-2 text-[11px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
        <Icon {...ICON} size={14} aria-hidden />
        {title}
      </p>
      {children}
    </Card>
  );
}

/**
 * Banner da obra.
 *
 * A foto da obra é o que identifica o lugar mais rápido que qualquer texto —
 * o fiscal reconhece o canteiro antes de ler o nome. Sobre ela vai uma tampa
 * chapada, não um gradiente: o degradê depende de onde o assunto da foto está,
 * e foto de campo não tem composição previsível.
 */
function ObraBanner({
  obra,
  onOpenPhoto,
}: {
  obra: ObraDetail;
  onOpenPhoto?: (() => void) | undefined;
}) {
  return (
    <div className="group relative h-[200px] overflow-hidden rounded-lg bg-foreground sm:h-[260px]">
      {obra.coverUrl ? (
        <>
          <img src={obra.coverUrl} alt="" className="absolute inset-0 size-full object-cover" />
          <div className="absolute inset-0 bg-[rgba(9,9,11,0.55)]" aria-hidden />
        </>
      ) : (
        <div className="absolute inset-0 grid place-items-center">
          <HardHat {...ICON} size={36} className="text-white/25" aria-hidden />
        </div>
      )}

      {/*
       * O botão cobre o banner inteiro, atrás do conteúdo, e o conteúdo não
       * captura ponteiro: clicar em qualquer ponto abre a foto, como na galeria.
       * Um <button> envolvendo o <h1> seria semanticamente errado.
       */}
      {onOpenPhoto ? (
        <>
          <button
            type="button"
            onClick={onOpenPhoto}
            aria-label="Ver foto da obra"
            className="absolute inset-0 z-0"
          />
          <span
            className="pointer-events-none absolute top-4 right-4 z-10 grid size-9 place-items-center rounded-sm bg-white/15 text-white opacity-0 transition-opacity group-hover:opacity-100"
            aria-hidden
          >
            <Expand size={16} strokeWidth={1.75} />
          </span>
        </>
      ) : null}

      <div className="pointer-events-none relative z-10 flex h-full flex-col justify-end gap-3 p-5 sm:p-7">
        <div className="flex flex-wrap items-center gap-2">
          <ObraStatusBadge status={obra.status} />
          {obra.isReform ? <Badge tone="primary">Reforma</Badge> : null}
          <VisitStatusBadge status={obra.visitStatus} />
        </div>

        <div>
          <h1 className="max-w-[40ch] text-[24px] leading-tight font-semibold tracking-tight text-white sm:text-[30px]">
            {obra.name}
          </h1>

          <p className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px] text-white/70">
            {obra.contractNumber ? (
              <span className="font-mono">Contrato {obra.contractNumber}</span>
            ) : (
              <span>Sem nº de contrato</span>
            )}
            {obra.company ? (
              <>
                <span aria-hidden>·</span>
                <span>{obra.company.tradeName ?? obra.company.legalName}</span>
              </>
            ) : null}
            {obra.city ? (
              <>
                <span aria-hidden>·</span>
                <span>
                  {obra.city}
                  {obra.state ? `/${obra.state}` : ''}
                </span>
              </>
            ) : null}
          </p>
        </div>
      </div>
    </div>
  );
}

export function ObraDetailPage() {
  // `protected` é rota sem caminho (só guarda de sessão), mas compõe o id da filha.
  const { obraId } = useParams({ from: '/protected/obras/$obraId' });
  const navigate = useNavigate();
  const { can, may } = useAuth();

  const [tab, setTab] = useState<TabId>('visao');
  const [editOpen, setEditOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [verRemovidos, setVerRemovidos] = useState(false);
  const [removendo, setRemovendo] = useState<ReportSummary | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erroDaRemocao, setErroDaRemocao] = useState<string | null>(null);
  const [restaurando, setRestaurando] = useState<ReportSummary | null>(null);
  const [erroDaRestauracao, setErroDaRestauracao] = useState<string | null>(null);
  const lightbox = useLightbox();

  /*
   * Quem remove enxerga o que removeu, e quem responde pelo histórico da obra
   * também. É a mesma regra do servidor — repetida aqui só para não oferecer um
   * interruptor que voltaria sem nada.
   */
  const podeVerRemovidos = can('gestor') || may('canDeleteReports');

  const { data: obra, isPending, isError, error, refetch } = useObra(obraId);
  const reports = useReports({
    obraId,
    perPage: 100,
    includeDeleted: verRemovidos && podeVerRemovidos,
  });
  const remove = useDeleteObra();
  const removerRelatorio = useRemoveReport(obraId);
  const restaurarRelatorio = useRestoreReport(obraId);

  async function confirmarRestauracao(): Promise<void> {
    if (!restaurando) return;

    setErroDaRestauracao(null);

    try {
      await restaurarRelatorio.mutateAsync(restaurando.id);
      setRestaurando(null);
    } catch (erro) {
      setErroDaRestauracao(erro instanceof Error ? erro.message : 'Não foi possível restaurar.');
    }
  }

  async function confirmarRemocao(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!removendo) return;

    setErroDaRemocao(null);

    try {
      await removerRelatorio.mutateAsync({ reportId: removendo.id, reason: motivo.trim() });
      setRemovendo(null);
      setMotivo('');
    } catch (erro) {
      setErroDaRemocao(erro instanceof Error ? erro.message : 'Não foi possível remover.');
    }
  }

  if (isPending) {
    return (
      <AppShell>
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-4 h-[200px] rounded-lg sm:h-[260px]" />
        <Skeleton className="mt-5 h-10 w-full max-w-[620px] rounded-md" />
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-[118px] rounded-lg" />
          ))}
        </div>
      </AppShell>
    );
  }

  if (isError) {
    return (
      <AppShell>
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      </AppShell>
    );
  }

  const paidRatio = ratio(obra.paidValueCents, obra.currentValueCents);
  const contractDays =
    obra.contractStart && obra.contractEnd
      ? daysBetween(obra.contractStart, obra.contractEnd)
      : null;
  const osDays = obra.osStart && obra.osEnd ? daysBetween(obra.osStart, obra.osEnd) : null;

  const newReportButton = can('fiscal') ? (
    <Button
      variant="primary"
      icon={<Plus {...ICON} size={16} />}
      onClick={() => setReportOpen(true)}
    >
      Novo relatório
    </Button>
  ) : null;

  const remaining = obra.currentValueCents - obra.paidValueCents - obra.glossValueCents;

  /*
   * O banner abre o mesmo acervo da galeria, e não só a capa: quem clica na
   * foto quer ver a obra, não aquele arquivo específico. A capa costuma ser a
   * primeira foto do relatório mais recente, então normalmente já está na
   * lista; quando não está — capa enviada à parte — entra como primeiro slide.
   */
  const photos = (reports.data?.items ?? [])
    .flatMap((report) =>
      report.media
        .filter((item) => item.mime.startsWith('image/'))
        .map((item) => ({ ...item, reportTitle: report.title, visitDate: report.visitDate })),
    )
    .sort((a, b) => b.visitDate.localeCompare(a.visitDate));

  const slides: LightboxItem[] = photos.map((photo) => ({
    id: photo.id,
    url: photo.url,
    title: photo.reportTitle,
    meta: [
      formatDateTime(photo.takenAt) !== '—'
        ? formatDateTime(photo.takenAt)
        : formatDate(photo.visitDate),
      photo.latitude != null && photo.longitude != null
        ? `${formatCoordinate(photo.latitude, 'lat')} ${formatCoordinate(photo.longitude, 'lng')}`
        : null,
    ].filter((line): line is string => Boolean(line)),
  }));

  const coverIndex = obra.coverUrl ? slides.findIndex((slide) => slide.url === obra.coverUrl) : -1;

  const bannerSlides: LightboxItem[] =
    coverIndex >= 0 || !obra.coverUrl
      ? slides
      : [{ id: 'capa', url: obra.coverUrl, title: obra.name, meta: ['capa da obra'] }, ...slides];

  const bannerStart = coverIndex >= 0 ? coverIndex : 0;

  return (
    <AppShell>
      {/* Trilha e ações ficam fora do banner: botão sobre foto nunca tem contraste previsível. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <nav aria-label="Trilha" className="flex items-center gap-1.5 text-[13px]">
          <Link
            to="/obras"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            Obras
          </Link>
          <span className="text-muted-foreground" aria-hidden>
            ›
          </span>
          <span className="max-w-[40ch] truncate">{obra.name}</span>
        </nav>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            icon={<ArrowLeft {...ICON} size={16} />}
            onClick={() => void navigate({ to: '/obras' })}
          >
            Voltar
          </Button>
          {/*
           * O servidor diz se esta pessoa edita esta obra (ADR 052): a regra
           * depende da obra e do alcance da caixinha, e repeti-la aqui faria o
           * botão aparecer onde o `PATCH` recusa — era o que acontecia quando
           * ele seguia só o papel.
           */}
          {obra.viewerCanEdit ? (
            <Button icon={<Pencil {...ICON} size={16} />} onClick={() => setEditOpen(true)}>
              Editar
            </Button>
          ) : null}
          {can('admin') ? (
            <Button
              variant="ghost"
              icon={<Trash2 {...ICON} size={16} />}
              onClick={() => setDeleteOpen(true)}
              className="text-danger hover:bg-danger-bg hover:text-danger"
            >
              Remover
            </Button>
          ) : null}
          {newReportButton}
        </div>
      </div>

      <ObraBanner
        obra={obra}
        onOpenPhoto={bannerSlides.length > 0 ? () => lightbox.open(bannerStart) : undefined}
      />

      <Lightbox
        items={bannerSlides}
        index={lightbox.index}
        onClose={lightbox.close}
        onNavigate={lightbox.navigate}
      />

      <div className="my-5">
        <TabList label="Seções da obra">
          {TABS.map((item) => {
            const count =
              item.value === 'relatorios'
                ? reports.data?.total
                : item.value === 'galeria'
                  ? reports.data?.items.reduce(
                      (sum, report) =>
                        sum + report.media.filter((m) => m.mime.startsWith('image/')).length,
                      0,
                    )
                  : undefined;

            return (
              <Tab
                key={item.value}
                active={tab === item.value}
                onClick={() => setTab(item.value)}
                icon={item.icon}
                disabled={Boolean(item.phase)}
                {...(item.phase ? { title: `Disponível na ${item.phase}` } : {})}
                {...(count ? { count } : {})}
              >
                {item.label}
              </Tab>
            );
          })}
        </TabList>
      </div>

      {tab === 'visao' ? (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi
              label="Valor atual"
              value={formatCurrency(obra.currentValueCents)}
              hint={
                obra.currentValueCents === obra.initialValueCents
                  ? 'sem aditivo ou supressão'
                  : `inicial ${formatCurrency(obra.initialValueCents)}`
              }
            />
            <Kpi
              label="Executado"
              value={formatPercent(paidRatio)}
              hint={formatCurrency(obra.paidValueCents)}
              tone="success"
            />
            <Kpi
              label="Saldo a executar"
              value={formatCurrency(Math.max(remaining, 0))}
              hint={
                obra.glossValueCents > 0
                  ? `${formatCurrency(obra.glossValueCents)} em glosa`
                  : undefined
              }
            />
            <Kpi
              label="Próxima visita"
              value={obra.nextVisitDueAt ? formatDate(obra.nextVisitDueAt) : '—'}
              hint={`a cada ${formatDays(obra.visitIntervalDays)}`}
              tone="inverted"
            />
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <div className="flex flex-col gap-3 lg:col-span-2">
              <Card className="p-5">
                <h2 className="text-[15px] font-semibold tracking-tight">Objeto</h2>
                <p className="mt-2 max-w-[68ch] leading-relaxed whitespace-pre-line text-muted-foreground">
                  {obra.description ?? 'Nenhuma descrição registrada para esta obra.'}
                </p>
              </Card>

              <Card>
                <CardHeader
                  title="Contrato e Ordem de Serviço"
                  description="Vigência do contrato e prazo de execução são contados separadamente."
                />
                <div className="grid gap-x-8 px-5 pb-3 sm:grid-cols-2">
                  <div>
                    <p className="mb-1 text-[11px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
                      Contrato
                    </p>
                    <DataRow label="Número" value={obra.contractNumber ?? '—'} />
                    <DataRow label="Início" value={formatDate(obra.contractStart)} />
                    <DataRow label="Fim" value={formatDate(obra.contractEnd)} />
                    <DataRow
                      label="Duração"
                      value={contractDays !== null ? formatDays(contractDays) : '—'}
                    />
                  </div>

                  {/* Empilhado no celular, o segundo bloco precisa de respiro próprio. */}
                  <div className="mt-4 sm:mt-0">
                    <p className="mb-1 text-[11px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
                      Ordem de serviço
                    </p>
                    <DataRow label="Início" value={formatDate(obra.osStart)} />
                    <DataRow label="Fim" value={formatDate(obra.osEnd)} />
                    <DataRow label="Duração" value={osDays !== null ? formatDays(osDays) : '—'} />
                  </div>
                </div>

                <div className="px-5 pb-5">
                  <p className="mb-2 text-[13px] text-muted-foreground">
                    Prazo de contrato decorrido
                  </p>
                  <ProgressBar
                    value={obra.timeProgress}
                    label="Prazo decorrido"
                    tone={obra.timeProgress >= 1 ? 'danger' : 'neutral'}
                  />
                </div>
              </Card>
            </div>

            <div className="flex flex-col gap-3">
              <SideCard title="Empresa contratada" icon={Building2}>
                {obra.company ? (
                  <div>
                    <p className="text-[14px] font-medium">
                      {obra.company.tradeName ?? obra.company.legalName}
                    </p>
                    {obra.company.tradeName ? (
                      <p className="mt-0.5 text-[13px] text-muted-foreground">
                        {obra.company.legalName}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-[13px] text-muted-foreground">Nenhuma empresa vinculada.</p>
                )}
              </SideCard>

              <SideCard title="Fiscalização" icon={UserCog}>
                {obra.fiscais.length === 0 ? (
                  <p className="text-[13px] text-muted-foreground">Nenhum fiscal designado.</p>
                ) : (
                  <ul className="flex flex-col gap-2.5">
                    {obra.fiscais.map((fiscal) => (
                      <li key={fiscal.id} className="flex items-center gap-2.5">
                        <Avatar name={fiscal.name} url={fiscal.avatarUrl} size={28} />
                        <span className="min-w-0 flex-1 truncate text-[13px]">{fiscal.name}</span>
                        {fiscal.role === 'responsavel' ? (
                          <Badge tone="primary">Responsável</Badge>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </SideCard>

              <SideCard title="Localização" icon={MapPin}>
                {obra.latitude != null && obra.longitude != null ? (
                  <div>
                    <p className="font-mono text-[13px]">
                      {formatCoordinate(obra.latitude, 'lat')}{' '}
                      {formatCoordinate(obra.longitude, 'lng')}
                    </p>
                    <p className="mt-1 text-[13px] text-muted-foreground">
                      {[obra.address, obra.city, obra.state].filter(Boolean).join(' · ') ||
                        'Endereço não informado'}
                    </p>
                    <a
                      href={`https://www.openstreetmap.org/?mlat=${obra.latitude}&mlon=${obra.longitude}#map=17/${obra.latitude}/${obra.longitude}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mt-2 inline-block text-[13px] font-medium text-primary-strong transition-colors hover:text-primary"
                    >
                      Abrir no mapa
                    </a>
                  </div>
                ) : (
                  <p className="text-[13px] text-muted-foreground">
                    Coordenadas não informadas. O mapa do portfólio depende delas.
                  </p>
                )}
              </SideCard>

              {/* Sem repetir a próxima visita: ela já é um dos indicadores acima. */}
              <SideCard title="Acompanhamento" icon={CalendarClock}>
                <DataRow label="Última visita" value={formatDate(obra.lastVisitAt)} />
                <DataRow label="Intervalo de visita" value={formatDays(obra.visitIntervalDays)} />
                <DataRow label="Relatórios" value={String(reports.data?.total ?? 0)} />
                <DataRow
                  label="Paralisações"
                  value={obra.status === 'paralisada' ? 'em vigor' : '—'}
                />
              </SideCard>
            </div>
          </div>
        </div>
      ) : null}

      {tab === 'financeiro' ? <ObraFinanceTab obra={obra} /> : null}

      {/* Sem repetir "Novo relatório": o botão já está no topo da página. */}
      {tab === 'relatorios' ? (
        <>
          {/*
            O interruptor só aparece para quem pode ver removidos, e fica
            discreto: o normal é o histórico do que aconteceu, e conferir
            remoção é visita ocasional.
          */}
          {podeVerRemovidos ? (
            <div className="mb-4 flex justify-end">
              <button
                type="button"
                onClick={() => setVerRemovidos((antes) => !antes)}
                aria-pressed={verRemovidos}
                className={cn(
                  'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] transition-colors',
                  verRemovidos
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {verRemovidos ? (
                  <Eye {...ICON} size={15} aria-hidden />
                ) : (
                  <EyeOff {...ICON} size={15} aria-hidden />
                )}
                {verRemovidos ? 'Ocultar removidos' : 'Mostrar removidos'}
              </button>
            </div>
          ) : null}

          <ReportTimeline
            reports={reports.data?.items ?? []}
            loading={reports.isPending}
            emptyAction={newReportButton}
            {...(may('canDeleteReports')
              ? {
                  onRemove: (report: ReportSummary) => setRemovendo(report),
                  onRestore: (report: ReportSummary) => setRestaurando(report),
                }
              : {})}
          />
        </>
      ) : null}

      {tab === 'galeria' ? <ObraGalleryTab reports={reports.data?.items ?? []} /> : null}

      {tab === 'paralisacoes' ? <ObraStoppagesTab obra={obra} /> : null}

      {tab === 'equipe' ? <ObraTeamTab obra={obra} /> : null}

      {tab === 'documentos' ? <ObraFilesTab obraId={obra.id} /> : null}

      <ObraFormModal open={editOpen} onClose={() => setEditOpen(false)} obra={obra} />
      <ReportFormModal open={reportOpen} onClose={() => setReportOpen(false)} obra={obra} />

      <Modal
        open={removendo !== null}
        onClose={() => {
          setRemovendo(null);
          setErroDaRemocao(null);
        }}
        title="Remover relatório"
        description="O registro sai do histórico, mas continua visível a quem administra, riscado."
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setRemovendo(null)}
              disabled={removerRelatorio.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              form="remover-relatorio"
              type="submit"
              loading={removerRelatorio.isPending}
              icon={<Trash2 {...ICON} size={16} />}
            >
              Remover relatório
            </Button>
          </>
        }
      >
        <form
          id="remover-relatorio"
          onSubmit={(event) => void confirmarRemocao(event)}
          className="flex flex-col gap-4"
          noValidate
        >
          {erroDaRemocao ? (
            <div
              role="alert"
              className="rounded-md bg-danger-bg px-3.5 py-3 text-[13px] text-danger"
            >
              {erroDaRemocao}
            </div>
          ) : null}

          <p className="text-[13px] text-muted-foreground">
            <strong className="font-semibold text-foreground">{removendo?.title}</strong>, de{' '}
            {formatDate(removendo?.visitDate ?? null)}.
          </p>

          <TextAreaField
            label="Motivo da remoção"
            required
            rows={4}
            hint="Fica ao lado do relatório riscado e na auditoria. Mínimo de 10 caracteres."
            value={motivo}
            onChange={(event) => setMotivo(event.target.value)}
          />
        </form>
      </Modal>

      <Modal
        open={restaurando !== null}
        onClose={() => {
          setRestaurando(null);
          setErroDaRestauracao(null);
        }}
        title="Restaurar relatório"
        description="O registro volta ao histórico da obra com os anexos, e o prazo de visita é recalculado."
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setRestaurando(null)}
              disabled={restaurarRelatorio.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={restaurarRelatorio.isPending}
              icon={<ArchiveRestore {...ICON} size={16} />}
              onClick={() => void confirmarRestauracao()}
            >
              Restaurar relatório
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {erroDaRestauracao ? (
            <div
              role="alert"
              className="rounded-md bg-danger-bg px-3.5 py-3 text-[13px] text-danger"
            >
              {erroDaRestauracao}
            </div>
          ) : null}

          <p className="text-[13px] text-muted-foreground">
            <strong className="font-semibold text-foreground">{restaurando?.title}</strong>, de{' '}
            {formatDate(restaurando?.visitDate ?? null)}.
            {restaurando && restaurando.mediaCount > 0
              ? ` ${restaurando.mediaCount === 1 ? '1 anexo volta' : `${restaurando.mediaCount} anexos voltam`} a aparecer.`
              : ''}
          </p>
        </div>
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Remover obra"
        description="A obra sai das listagens, mas o histórico e a auditoria são preservados."
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDeleteOpen(false)}
              disabled={remove.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              icon={<Trash2 {...ICON} size={16} />}
              onClick={() => {
                remove.mutate(obra.id, {
                  onSuccess: () => void navigate({ to: '/obras' }),
                });
              }}
            >
              Remover obra
            </Button>
          </>
        }
      >
        <p className="text-muted-foreground">
          Confirma a remoção de <strong className="font-semibold">{obra.name}</strong>? A exclusão é
          lógica e pode ser revertida por um administrador diretamente no banco.
        </p>
      </Modal>
    </AppShell>
  );
}
